
import { useEffect, useState } from "react";
import {
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  LinearProgress,
  Box,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../../context/AuthContext";
import {
  getShopifyStatus,
  startShopifyOAuth,
  disconnectShopify,
  finishShopifyOAuth,
  syncShopifyCatalog,
  deleteShopifyShop,
} from "../../api/shopify";

const DOMAIN_SUFFIX = ".myshopify.com";

function normalizeShopDomain(input: string): string {
  let v = input.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, "").split("/")[0];
  v = v.replace(/\.admin\.shopify\.com$/, "");
  if (!v.endsWith(DOMAIN_SUFFIX)) {
    if (/^[a-z0-9][a-z0-9-]*$/.test(v)) v = `${v}${DOMAIN_SUFFIX}`;
  }
  return v;
}

function isValidShopDomain(host: string): boolean {
  return /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(host);
}

const ShopifySettings = () => {
  const { currentUser } = useAuth();
  const [connected, setConnected] = useState(false);
  const [shopDomain, setShopDomain] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showMessage, setShowMessage] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      setLoading(true);
      const token = await currentUser.getIdToken(true);
      const data = await getShopifyStatus();
      if (data) {
        setConnected(!!data.connected);
        setShopDomain(data.shop_domain || null);
      }
      setLoading(false);
    })();
  }, [currentUser]);

  // handle redirect from Shopify OAuth
  useEffect(() => {
    (async () => {
      if (!currentUser) return;
      const query = new URLSearchParams(window.location.search);
      if (query.get("code") && query.get("shop") && query.get("state")) {
        setLoading(true);
        try {
          const token = await currentUser.getIdToken(true);
          const result = await finishShopifyOAuth(query);
          if (result.status === "connected") {
            setConnected(true);
            setShopDomain(result.shop_domain);
            setShowMessage("Shopify connected successfully");
            // ⚙️ Trigger initial catalog sync automatically
            setSyncing(true);
            await syncShopifyCatalog();
            setShowMessage("Catalog synced successfully");
          }
          window.history.replaceState({}, "", window.location.pathname);
        } catch (err: any) {
          console.error("❌ Shopify callback error:", err);
          setShowMessage(err.message || "Failed to connect Shopify");
        } finally {
          setSyncing(false);
          setLoading(false);
        }
      }
    })();
  }, [currentUser]);

  const openConnectDialog = () => {
    setInput("");
    setError(null);
    setDialogOpen(true);
  };

  const submitConnect = async () => {
    if (!currentUser) return;
    const normalized = normalizeShopDomain(input);
    if (!isValidShopDomain(normalized)) {
      setError("Enter a valid domain like mystore.myshopify.com");
      return;
    }
    try {
      setLoading(true);
      const token = await currentUser.getIdToken(true);
      await startShopifyOAuth(normalized);
    } catch (err: any) {
      console.error("❌ Shopify connect error:", err);
      setShowMessage(err.message || "Failed to start Shopify connection");
      setLoading(false);
    }
  };

  const handleDisconnect = () => {
    setDisconnectDialogOpen(true);
  };

  const confirmDisconnect = async () => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken(true);
    try {
      setLoading(true);
      await disconnectShopify();
      setConnected(false);
      setShopDomain(null);
      setShowMessage("Shopify disconnected");
    } catch (err: any) {
      console.error("❌ Shopify disconnect error:", err);
      setShowMessage(err.message || "Failed to disconnect Shopify");
    } finally {
      setLoading(false);
      setDisconnectDialogOpen(false);
    }
  };

  // 🧩 Manual sync
  const handleSync = async () => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken(true);
    try {
      setSyncing(true);
      await syncShopifyCatalog();
      setShowMessage("Catalog synced successfully");
    } catch (err: any) {
      console.error("❌ Catalog sync error:", err);
      setShowMessage(err.message || "Catalog sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // 🧨 Delete shop permanently (optional future)
  const handleDeleteShop = async () => {
    if (!currentUser) return;
    const token = await currentUser.getIdToken(true);
    try {
      setLoading(true);
      await deleteShopifyShop();
      setConnected(false);
      setShopDomain(null);
      setShowMessage("Shop deleted permanently");
    } catch (err: any) {
      console.error("❌ Delete shop error:", err);
      setShowMessage(err.message || "Failed to delete shop");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Shopify Integration
      </Typography>

      {connected ? (
        <Typography variant="body1" sx={{ mb: 1 }}>
          Connected to <strong>{shopDomain}</strong>
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Shopify store to sync products and manage your catalog.
        </Typography>
      )}

      {syncing && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Syncing your catalog, please wait...
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
        <Button
          variant="contained"
          color={connected ? "secondary" : "primary"}
          onClick={connected ? handleDisconnect : openConnectDialog}
          disabled={loading || syncing}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {connected ? "Disconnect Shopify" : "Connect Shopify"}
        </Button>

        {connected && (
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={syncing || loading}
            >
              Sync Catalog
            </Button>

            <Tooltip title="Permanently delete shop and all data">
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteShop}
                disabled={loading || syncing}
              >
                Delete Shop
              </Button>
            </Tooltip>
          </>
        )}
      </Box>


      {/* Connect dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Connect your Shopify store
          <Tooltip title="You can find your shop domain in Shopify Admin → Settings → Domains. It usually looks like mystore.myshopify.com.">
            <IconButton size="small" sx={{ ml: "auto" }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label="Shop domain"
            placeholder="mystore.myshopify.com"
            fullWidth
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitConnect();
            }}
            error={!!error}
            helperText={
              error ||
              "Paste your full .myshopify.com domain. We’ll fix common formats automatically."
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="caption" color="text.secondary">
                    .myshopify.com
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitConnect} disabled={loading}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Disconnect Shopify</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Are you sure you want to disconnect your Shopify store{" "}
            {shopDomain && <strong>{shopDomain}</strong>}?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will remove your store’s connection from Traderly. You can reconnect
            anytime later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDisconnect}
            variant="contained"
            color="error"
            disabled={loading}
          >
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={!!showMessage}
        autoHideDuration={4000}
        onClose={() => setShowMessage(null)}
        message={showMessage}
      />
    </>
  );
};

export default ShopifySettings;
